/**
 * Cookie consent banner — GDPR/PECR-style. Shows once until user accepts or rejects.
 * Stores choice in localStorage under 'gl_cookie_consent' ('accepted' | 'rejected').
 * Until accepted, optional analytics tracking should remain disabled.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';

const STORAGE_KEY = 'gl_cookie_consent';

export function getConsent() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_KEY);
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Show after a short delay so the page can paint first
    const t = setTimeout(() => {
      if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
    }, 900);
    return () => clearTimeout(t);
  }, []);

  function decide(value) {
    localStorage.setItem(STORAGE_KEY, value);
    setOpen(false);
  }

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-40 bg-charcoal text-base rounded-3xl shadow-2xl border border-base/10 grain"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start gap-3">
          <Cookie size={22} className="text-mustard shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display text-lg leading-tight mb-1">A quick word about cookies.</div>
            <p className="text-base/80 text-sm leading-relaxed mb-4">
              We use essential cookies to make the site work, plus anonymous visit statistics
              (no advertising, no profiling, never shared — UK DUAA 2025 statistical purposes).
              Choose "Essential only" to switch statistics off. See our <Link to="/cookies" className="underline underline-offset-2 hover:text-mustard">cookie policy</Link>{' '}
              and <Link to="/privacy" className="underline underline-offset-2 hover:text-mustard">privacy notice</Link>.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => decide('accepted')}
                data-testid="cookie-accept-btn"
                className="bg-mustard text-charcoal px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-base transition-colors"
              >
                Accept all
              </button>
              <button
                onClick={() => decide('rejected')}
                data-testid="cookie-reject-btn"
                className="bg-base/10 hover:bg-base/15 text-base/90 px-4 py-2.5 rounded-full text-sm font-semibold border border-base/15 transition-colors"
              >
                Essential only
              </button>
            </div>
          </div>
          <button
            onClick={() => decide('rejected')}
            aria-label="Close"
            className="text-base/60 hover:text-base p-1 -mr-1 -mt-1"
            data-testid="cookie-close-btn"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
